// Content for the CRS Damp Survey funnel page (/damp-survey).
// Mirrors the Home Damp Check homepage with CRS branding applied.
//
// Rule: Title Case for headings, eyebrows, CTAs, chips. Sentence case for
// body prose and FAQ answers. No em-dashes or en-dashes anywhere.

import hero1600 from "@/assets/damp-survey/hero-house-1600.webp";
import { rasterSrc } from "@/generated/raster-images";

const condensationImg = rasterSrc("damp-survey/damp-condensation");
const penetratingImg = rasterSrc("damp-survey/damp-penetrating");
const risingImg = rasterSrc("damp-survey/damp-rising");
const surveyPenetrating = rasterSrc("damp-survey/services/structural-penetrating-damp");
const surveyExternal = rasterSrc("damp-survey/services/external-repairs-management");
const surveyBreathable = rasterSrc("damp-survey/services/breathable-wall-moisture-control");
const surveyCavity = rasterSrc("damp-survey/services/cavity-insulation-assessment");
const surveyVentilation = rasterSrc("damp-survey/services/controlled-ventilation-air-quality");

export const DAMP_SURVEY_HERO_FLOAT_IMAGES = [
  { src: hero1600, slug: "damp-hero" },
  { src: condensationImg, slug: "damp-condensation" },
  { src: penetratingImg, slug: "damp-penetrating" },
  { src: risingImg, slug: "damp-rising" },
  { src: surveyPenetrating, slug: "damp-survey-penetrating" },
  { src: surveyExternal, slug: "damp-survey-external" },
  { src: surveyBreathable, slug: "damp-survey-breathable" },
  { src: surveyCavity, slug: "damp-survey-cavity" },
  { src: surveyVentilation, slug: "damp-survey-ventilation" },
];

export const DAMP_SURVEY_HERO = {
  h1: "Damp, Mould And Moisture Specialist Solutions",
  subtitle:
    "We look beyond the visible signs of damp and mould to identify the cause, recommend the right solution, and reduce the risk of the problem returning.",
  badges: [
    "Accredited And Insured",
    "No Obligation",
    "50+ Years Combined Experience",
  ],
  sampleCardEyebrow: "Want To See What's Included?",
  sampleCardBody:
    "See exactly what you'll receive after your damp assessment, including clear findings, photo evidence, likely causes and practical next steps.",
  sampleCardCta: "View Sample Report",
};

export const DAMP_SURVEY_HELP_STRIP = {
  title: "How CRS Energy Helps",
  intro:
    "Damp or mould can appear for many different reasons, and what you see on the surface isn't always where the problem begins.",
  cards: [
    {
      title: "We Find The Cause",
      body: "We inspect damp, mould, condensation and moisture problems to understand what is really happening in your home.",
    },
    {
      title: "You Get Clear Answers",
      body: "We explain the likely cause, what needs attention, and which solutions are most suitable for your property.",
    },
    {
      title: "We Help Fix It Properly",
      body: "Where work is needed, we can guide you through practical solutions designed to resolve the issue and reduce the risk of it returning.",
    },
  ],
  chips: [
    "Guaranteed Workmanship",
    "Fully Accredited",
    "Precision Installs",
    "Report Led Works",
  ],
};

export const DAMP_SURVEY_SAMPLE_REPORT_INTRO = {
  eyebrow: "Sample Report",
  heading: "See What Your Damp Survey Report Looks Like",
  intro:
    "A fictional example so you can see the structure, the level of detail and the kind of evidence we use. Your own report will be based entirely on what is observed at your property.",
};

export const DAMP_TYPE_CARDS = [
  {
    title: "Condensation Damp",
    image: condensationImg,
    imageAlt: "Condensation droplets on a domestic window",
    explanation:
      "Condensation happens when warm, moisture laden air meets colder surfaces, often leading to water droplets, mould and musty smells.",
    signs: [
      "Water droplets on windows or glazing",
      "Damp surfaces in colder months",
      "Black mould in corners or behind furniture",
      "Musty odours in enclosed rooms",
    ],
    howWeCheck:
      "We review humidity, ventilation, airflow, cold surfaces and how the room is being used before recommending the right next step.",
  },
  {
    title: "Penetrating Damp",
    image: penetratingImg,
    imageAlt: "External brickwork with weathered pointing near a downpipe",
    explanation:
      "Penetrating damp can happen when water enters from outside through defects such as brickwork, pointing, render, gutters, roof junctions or window areas.",
    signs: [
      "Localised damp patches on walls or ceilings",
      "Damp that worsens after rainfall",
      "Peeling paint or plaster deterioration",
      "Staining near windows, chimneys or external walls",
    ],
    howWeCheck:
      "We review the outside of the property, rainwater routes, visible defects, moisture patterns and the areas affected inside.",
  },
  {
    title: "Rising Damp",
    image: risingImg,
    imageAlt: "Lower portion of an interior wall meeting a skirting board",
    explanation:
      "Rising damp is associated with moisture moving up from ground level, often showing as low level staining, salts or damage near skirting boards.",
    signs: [
      "Low level dampness, often up to 1 metre",
      "Salt deposits on plaster surfaces",
      "Tide marks on internal walls",
      "Deteriorating skirting boards",
    ],
    howWeCheck:
      "We review moisture levels, salts, wall construction, ground levels, bridging and whether another cause could be creating similar symptoms.",
  },
] as const;

export const DAMP_SURVEY_PROCESS = {
  title: "From First Check To The Right Fix",
  intro:
    "We keep the process simple: understand the symptoms, identify the likely cause, then recommend the right solution for your home.",
  steps: [
    {
      n: 1,
      headline: "Tell Us What's Happening",
      body: "Start by sharing what you're seeing, damp patches, mould, condensation, musty smells or recurring moisture issues.",
      outcome: "Initial guidance on the likely next step.",
    },
    {
      n: 2,
      headline: "We Inspect The Cause",
      body: "Where needed, we assess moisture patterns, ventilation, building fabric, insulation and possible water entry points.",
      outcome: "Clear findings and practical recommendations.",
    },
    {
      n: 3,
      headline: "We Help Put It Right",
      body: "If work is needed, our CRS Energy team can guide you through suitable solutions, from ventilation and wall repairs to external works or insulation improvements.",
      outcome:
        "A targeted plan designed to deal with the cause and reduce the risk of the problem returning.",
    },
  ],
  trustHeadline: "Why It Matters: Evidence First, No Obligation",
  trustBody:
    "Every recommendation we make traces back to something we observed at your property. We assess your home as a whole system, tell you what needs attention and what doesn't, and hand you a written report you own outright.",
  trustPillars: [
    {
      title: "Evidence-Led Diagnosis",
      desc: "Findings trace back to a physical observation at your property, not guesswork.",
    },
    {
      title: "Proportionate, Not Padded",
      desc: "Not every home needs extensive work. Where it doesn't, we say so. Where it does, we explain why.",
    },
    {
      title: "You Own The Report",
      desc: "Share it with any contractor, solicitor or insurer. No lock-in.",
    },
  ],
  reportHeadline: "What Does An Evidence Led Report Contain?",
  reportBody:
    "Every recommendation is traceable back to a physical observation made on site.",
  reportSections: [
    "Property Overview",
    "Moisture Observations",
    "Diagnostic Analysis",
    "Recommended Actions",
    "What Is NOT Required",
    "Surveyor Declaration",
  ],
  bottomCtaHeadline: "Ready To Understand What's Causing The Problem?",
  bottomCtaBody:
    "Start with a few details and we'll help guide the right next step.",
};

export const DAMP_SURVEY_PRICING = {
  heading: "Assessment And Report Packages",
  intro:
    "Following your initial call, if an on site assessment is recommended we offer two professional survey options. You own the resulting report, and there is no obligation to proceed.",
  deductionBanner:
    "100% Cost Deduction: The cost of your chosen survey is fully deducted from your final bill if you proceed with our recommended remediation works. You never pay twice.",
  essential: {
    title: "Essential Assessment And Mini Report",
    price: "£99",
    subtitle: "Perfect for a fast diagnosis and exact repair costs.",
    bullets: [
      "On site root cause diagnosis",
      "Clear verbal summary and severity rating",
      "Concise written mini report",
      "Firm, no obligation quotation for repairs",
    ],
    badge: "£99 deducted from your repair bill",
    cta: "Choose Essential",
  },
  full: {
    title: "Full Diagnostic Damp Survey",
    price: "£249",
    subtitle: "Ideal for lenders, insurers, or complex disputes.",
    badgeLabel: "Most Comprehensive",
    includesPrefix: "Everything in Essential, plus:",
    bullets: [
      "Thermal imaging cold spot mapping",
      "Per room moisture readings (WME%)",
      "Photographic evidence and annotations",
      "Signed formal report valid for 3 months",
    ],
    badge: "£249 deducted from your repair bill",
    cta: "Choose Full Survey",
  },
  factorsHeading: "What Affects Remediation Cost?",
  factors: [
    { label: "Property Size And Layout", desc: "Total m² and room configuration" },
    { label: "Rooms Affected", desc: "Number of rooms and spread of symptoms" },
    { label: "Construction Type", desc: "Solid wall, cavity, extensions" },
    { label: "Access Requirements", desc: "External access and inspection needs" },
    { label: "Complexity", desc: "Prior treatments, multiple elevations, basements" },
    { label: "Your Priorities", desc: "Targeted rooms first vs whole home route" },
  ],
  factorsFootnote:
    "Not every home needs extensive work. Where remedial measures are advised, we'll explain why they're needed, what problem they address, and which package is proportionate, so you can choose the route that fits.",
};

export const DAMP_SURVEY_FAQS = [
  {
    q: "What makes a CRS damp survey different from a standard quote?",
    a: "We don't provide estimates from limited information. A CRS surveyor attends on site, reviews moisture patterns, ventilation, building fabric and thermal performance, then issues a written report. Recommendations are tailored to your property's unique build and behaviour, not pulled from a template.",
  },
  {
    q: "Why can damp and mould be misdiagnosed?",
    a: "Damp and mould can have a variety of sources, including condensation, penetrating damp, rising damp, leaks and ventilation issues. Stains, musty smells or peeling paint can look similar but have very different causes. A thorough, professional investigation is the only reliable way to identify the true source of moisture rather than treat what's visible on the surface.",
  },
  {
    q: "How do you determine the true cause of damp?",
    a: "We examine the property's construction, moisture behaviour, ventilation and thermal performance using industry standard assessment techniques. This goes beyond a surface inspection and aims to understand moisture movement, condensation patterns and potential structural issues. Recommendations are only made once the underlying cause is understood.",
  },
  {
    q: "Isn't mould just a cosmetic issue?",
    a: "Mould can initially appear as discoloration or spots on walls, but it usually points to an underlying moisture problem. Persistent mould can affect indoor air quality and, in some cases, occupant health, especially for those with allergies or respiratory conditions. Removing visible growth without addressing the moisture source rarely prevents it from returning.",
  },
  {
    q: "What types of moisture problems can you identify?",
    a: "We help identify the most common forms of damp and moisture issues, including:\n\nCondensation: moisture in the air settling on cooler surfaces such as windows or walls, often caused by poor ventilation or uneven heating.\nPenetrating damp: water entering through defects such as cracks or gaps in walls, roofs, windows or external features.\nRising damp: moisture moving up from the ground into walls through bricks and masonry. Less common, but often misdiagnosed.\n\nEach has different causes and different remedies, which is why correct identification matters.",
  },
  {
    q: "Will the surveyor just take moisture readings?",
    a: "No. A single moisture reading in isolation is often misleading, because damp problems can vary with weather, temperature and occupancy. Our surveyor interprets readings alongside construction details, ventilation, occupancy patterns and external conditions to build an accurate picture.",
  },
  {
    q: "Do you carry out the remediation works yourselves?",
    a: "Yes. CRS Energy delivers remedial works in house using accredited teams, from ventilation upgrades and wall repairs to external works and insulation improvements. Where a specialist sub trade is needed, we use carefully selected certified contractors and manage the project end to end.",
  },
  {
    q: "What happens after the work is completed?",
    a: "You receive clear aftercare guidance, advice on maintaining healthy moisture levels, and warranties on applicable works. We support you long after the job is done to keep the underlying cause from returning.",
  },
  {
    q: "Can I prevent damp and mould issues myself?",
    a: "Improving ventilation, using humidity sensing extract fans and maintaining steady heating all help. Where structural or environmental factors are involved, a professional investigation is usually the safer route, because prevention starts with understanding your home's unique behaviour and moisture profile.",
  },
  {
    q: "What if my home still has issues after the work is done?",
    a: "We stand behind our recommendations and workmanship. If symptoms persist we'll reassess and adjust the plan to make sure the underlying cause has actually been resolved, not just temporarily masked.",
  },
  {
    q: "How does the initial assessment work?",
    a: "Start by telling us what you're seeing. If an on site survey is recommended we offer Essential (£99) and Full Diagnostic (£249) packages. The survey fee is deducted in full if you proceed with our recommended repairs.",
  },
  {
    q: "Is the survey fee deducted from remediation works?",
    a: "Yes. The full cost of your chosen survey is deducted from your final bill if you proceed with our recommended works.",
  },
  {
    q: "Can I focus on certain rooms only?",
    a: "Yes. We still assess the home as a system to avoid misdiagnosis, but recommendations can be phased so you can prioritise specific rooms first.",
  },
];

export const DAMP_SURVEY_CONTACT = {
  eyebrow: "Get In Touch",
  heading: "Get In Touch With The Experts",
  intro:
    "Share a few details and our damp survey team will respond quickly with the right next step for your property.",
  bullets: [
    "Clear next step within one working day",
    "Speak to a qualified damp specialist",
    "Covers Wales and the South West",
  ],
  topicOptions: [
    "Book A Damp Assessment",
    "Question About Damp Or Mould In My Home",
    "Quote Or Pricing Question",
    "Follow Up On An Existing Enquiry",
    "Other",
  ],
};

/* ──────────────── Diagnostic services ──────────────── */

export interface DiagnosticService {
  title: string;
  description: string;
}

export interface DiagnosticCategory {
  number: string;
  title: string;
  services: DiagnosticService[];
}

export const DIAGNOSTIC_SERVICES_META = {
  heading: "Our Full Diagnostic And Advisory Service Catalogue",
  intro:
    "Specialist diagnostic, testing, advisory and consultation services across nine categories. Use the arrows to explore each category.",
  ctaLabel: "Start My Damp Survey",
};

export const DIAGNOSTIC_CATEGORIES: DiagnosticCategory[] = [
  {
    number: "1",
    title: "Core Diagnostic Services",
    services: [
      { title: "Moisture Pathway Assessment", description: "A structured investigation that identifies where moisture is entering, how it is travelling through the building fabric, and why it is presenting in visible areas. We trace the full pathway from source to manifestation." },
      { title: "Full Damp, Mould And Condensation Survey", description: "A comprehensive room by room inspection covering moisture readings, ventilation review, heating patterns, building fabric condition and defect identification. Designed to distinguish between condensation, rising damp, penetrating damp or mixed moisture patterns." },
      { title: "RICS Aligned Damp Investigation", description: "An evidence led damp assessment following structured survey principles consistent with RICS guidance. Focuses on objective data, moisture profiling and root cause identification rather than visual assumptions." },
      { title: "Room By Room Moisture Profiling", description: "Detailed recording of surface and sub surface moisture readings across affected and unaffected areas. Helps determine whether moisture is localised, systemic or structural in origin." },
      { title: "Moisture Mapping And Pattern Analysis", description: "Visual mapping of damp patterns throughout the property to identify consistency with specific moisture mechanisms such as capillary rise, lateral ingress or condensation." },
      { title: "Building Fabric Moisture Analysis", description: "Assessment of wall construction, insulation type, render condition, pointing integrity and structural elements to understand how the building responds to moisture load." },
      { title: "Root Cause Damp Diagnosis", description: "A diagnostic process that separates primary moisture causes from secondary symptoms, preventing unnecessary or misdirected remedial work." },
      { title: "Evidence-Led Damp Assessment", description: "An assessment carried out without pre determined treatment outcomes. Recommendations are based solely on evidence gathered during inspection." },
    ],
  },
  {
    number: "2",
    title: "Technical Testing Services",
    services: [
      { title: "Surface And Substrate Moisture Meter Profiling", description: "Calibrated moisture meters used to assess surface and deeper moisture content, enabling comparison between affected and control areas." },
      { title: "Gravimetric Moisture Sampling", description: "Laboratory based moisture content testing used where precise quantification is required to confirm or rule out rising damp." },
      { title: "Hygroscopic Salt Analysis", description: "Evaluation of salt contamination within plaster to determine whether moisture marks are historic, active or misinterpreted." },
      { title: "Thermal Bridging Risk Review", description: "Assessment of areas prone to surface temperature drops, including corners, window reveals and uninsulated solid walls, to identify condensation risk." },
      { title: "Dew Point And Condensation Risk Analysis", description: "Analysis of internal temperature, humidity and surface conditions to determine whether condensation is likely to form under current occupancy patterns." },
      { title: "Ventilation Performance Assessment", description: "Evaluation of extractor functionality, airflow provision and background ventilation adequacy relative to occupancy and property size." },
    ],
  },
  {
    number: "3",
    title: "Condensation And IAQ Services",
    services: [
      { title: "Condensation Risk Assessment", description: "Identifies whether moisture symptoms are driven by internal humidity imbalance, ventilation restriction or cold surface temperature." },
      { title: "Indoor Air Quality Moisture Review", description: "Evaluates how occupancy, lifestyle and airflow influence humidity levels and mould risk." },
      { title: "Ventilation Deficit Identification", description: "Determines whether the property's ventilation infrastructure is insufficient to manage moisture load." },
      { title: "Heating Pattern Moisture Analysis", description: "Assesses whether intermittent or inadequate heating is contributing to surface condensation." },
      { title: "Seasonal Condensation Mapping", description: "Examines whether symptoms correlate with colder months, suggesting temperature driven moisture formation." },
      { title: "Cold Surface Risk Identification", description: "Pinpoints areas where wall temperatures fall below dew point, encouraging condensation formation." },
    ],
  },
  {
    number: "4",
    title: "Rising Damp Services",
    services: [
      { title: "Rising Damp Differentiation Survey", description: "Separates true capillary moisture rise from condensation or penetrating damp that mimic similar visual symptoms." },
      { title: "Damp Proof Course Integrity Review", description: "Inspects the presence, condition and effectiveness of the damp proof course and identifies potential bridging." },
      { title: "Ground Level And Bridging Assessment", description: "Reviews external ground levels, paving and render bridging that may allow moisture to bypass the damp proof course." },
      { title: "Capillary Moisture Movement Analysis", description: "Evaluates whether masonry moisture movement is consistent with upward capillary action." },
      { title: "Salt Contamination Review", description: "Determines whether hygroscopic salts are present, supporting or refuting a rising damp diagnosis." },
      { title: "Skirting And Low Level Timber Risk Assessment", description: "Inspects timber elements for moisture related deterioration at low level." },
    ],
  },
  {
    number: "5",
    title: "Penetrating Damp Services",
    services: [
      { title: "External Envelope Ingress Investigation", description: "Identifies defects in masonry, render, roofline or flashing that may be allowing water entry." },
      { title: "Masonry And Render Defect Assessment", description: "Evaluates cracked render, deteriorated pointing and porous brickwork." },
      { title: "Gutter And Rainwater Goods Impact Review", description: "Assesses how blocked or defective drainage systems contribute to damp symptoms." },
      { title: "Flashing And Roofline Inspection", description: "Reviews junction details that commonly allow lateral moisture penetration." },
      { title: "Wall Tie Moisture Risk Review", description: "Considers whether wall tie corrosion or failure may be contributing to moisture ingress." },
      { title: "Pointing And Joint Condition Assessment", description: "Evaluates mortar joint integrity and its effect on water resistance." },
    ],
  },
  {
    number: "6",
    title: "Mixed Moisture Services",
    services: [
      { title: "Multi Source Moisture Investigation", description: "Designed for properties exhibiting overlapping damp indicators across categories." },
      { title: "Complex Damp Diagnosis", description: "Used where multiple moisture pathways may be active simultaneously." },
      { title: "Overlapping Moisture Source Review", description: "Determines whether condensation is masking penetrating or rising damp." },
      { title: "Phased Remediation Planning", description: "Creates a sequenced repair strategy addressing primary causes before secondary symptoms." },
    ],
  },
  {
    number: "7",
    title: "Structural Risk Services",
    services: [
      { title: "Structural Moisture Damage Review", description: "Inspects whether prolonged moisture has compromised structural integrity." },
      { title: "Timber Decay Risk Assessment", description: "Evaluates the risk of wet rot or dry rot due to persistent damp." },
      { title: "Subfloor Moisture Evaluation", description: "Reviews ventilation and moisture levels beneath suspended timber floors." },
      { title: "Load Bearing Element Damp Risk Check", description: "Ensures moisture has not affected structural components." },
    ],
  },
  {
    number: "8",
    title: "Remedial Strategy Services",
    services: [
      { title: "Phased Remedial Planning", description: "Prioritises corrective actions in logical sequence to prevent recurrence." },
      { title: "Priority Order Repair Structuring", description: "Identifies which defects must be addressed first to avoid ineffective treatment." },
      { title: "Ventilation Upgrade Specification", description: "Recommends appropriate mechanical or passive ventilation improvements." },
      { title: "Damp Proofing Suitability Assessment", description: "Determines whether chemical damp proofing is appropriate, and only when evidence supports it." },
      { title: "External Envelope Repair Planning", description: "Specifies required masonry, render or roofline repairs." },
      { title: "Mechanical Ventilation Strategy Advisory", description: "Advises on extraction systems, airflow balance and humidity control solutions." },
    ],
  },
  {
    number: "9",
    title: "Consultation Services",
    services: [
      { title: "Diagnostic Triage Call", description: "Short structured consultation to review symptom data and advise on next steps." },
      { title: "Assessment Review Consultation", description: "Walkthrough of survey findings and explanation of moisture classification." },
      { title: "Report Walkthrough Session", description: "Clarifies recommendations and phased repair priorities." },
      { title: "Moisture Risk Clarification Call", description: "Used when self help measures are being considered but confirmation is needed." },
      { title: "Post Survey Advisory Session", description: "Follow up discussion after remedial works or report delivery." },
    ],
  },
];

/* ──────────────── Methodology ──────────────── */

export interface MethodologyPhase {
  num: number;
  label: string;
  subtitle: string;
  title: string;
  purpose: string;
  detailTitle: string;
  details: string[];
}

export const METHODOLOGY_META = {
  heading: "Our Diagnostic First Methodology",
  intro:
    "Moisture issues require careful investigation before any remedial work is considered. Our methodology follows a structured, building fabric led approach designed to identify the source before recommending a solution.",
};

export const METHODOLOGY_PHASES: MethodologyPhase[] = [
  {
    num: 1,
    label: "DISCOVERY",
    subtitle: "Discovery And Preliminary Analysis",
    title: "Understanding The Building's History",
    purpose:
      "Before visiting, we gather key details to shape an accurate diagnosis. Where information is unclear, we assist in establishing it through records. You are not expected to provide technical answers.",
    detailTitle: "What We Collect",
    details: [
      "Property age and type",
      "Pattern of symptoms",
      "Occupancy load",
      "Previous alterations",
      "Seasonal variation",
      "Known repair history",
    ],
  },
  {
    num: 2,
    label: "ASSESSMENT",
    subtitle: "Evidence-Led Forensic Assessment",
    title: "Diagnosis Before Repair",
    purpose:
      "A structured, evidence led on site investigation. Where multiple causes are possible, a differential diagnosis approach is applied against the physical evidence.",
    detailTitle: "What We Examine",
    details: [
      "External fabric condition",
      "Roofline and rainwater goods",
      "Cavity indicators",
      "Heating and thermal balance",
      "Masonry and pointing",
      "DPC and ground levels",
      "Ventilation paths",
      "Moisture gradients",
    ],
  },
  {
    num: 3,
    label: "ADVICE",
    subtitle: "Evidence Based Strategy",
    title: "Consultative Advice",
    purpose:
      "We translate verified findings into a proportionate remediation plan. The aim is to ensure works address the root cause, avoiding unnecessary or ineffective treatments.",
    detailTitle: "What We Recommend",
    details: [
      "Confirmed diagnosis",
      "Scope and limitations",
      "Why certain works are NOT required",
      "Measures aligned to causes",
      "Alternatives",
      "Sequencing of steps",
    ],
  },
  {
    num: 4,
    label: "REMEDIATION",
    subtitle: "Targeted Technical Remediation",
    title: "Targeted Technical Remediation",
    purpose:
      "Once confirmed, we implement agreed remedial measures designed to resolve the specific issue, using compatible materials and appropriate methods for the building's age.",
    detailTitle: "What We Deliver",
    details: [
      "External fabric repairs",
      "Cavity condition correction",
      "Breathable plaster systems",
      "Structural defect rectification",
      "DPC adjustments",
      "Ventilation improvements",
    ],
  },
  {
    num: 5,
    label: "VALIDATION",
    subtitle: "Validation And Long Term Health",
    title: "Verified Results",
    purpose:
      "Following completion, outcomes are reviewed against original findings to confirm moisture behaviour has stabilised. We also issue warranties and ongoing maintenance guidance.",
    detailTitle: "What We Validate",
    details: [
      "Confirmation against diagnosis",
      "Identification of residual risks",
      "Warranty documentation",
      "Review of moisture performance",
      "Maintenance guidance",
      "Long term monitoring advice",
    ],
  },
];
